import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calculator, LoaderCircle, ShieldCheck, TrendingUp } from 'lucide-react';
import { authApi } from '@/lib/api/auth';
import { normalizeNationalMobileDigits } from '@/lib/auth/normalizeNationalMobile';

const RESEND_SECONDS = 90;
const OTP_LENGTH = 5;
const FLOAT_SYMBOLS: { ch: string; className: string; style?: React.CSSProperties }[] = [
  { ch: '∑', className: 'top-[6%] right-[8%] text-4xl sm:text-5xl animate-math-float' },
  {
    ch: '∫',
    className: 'top-[18%] left-[4%] text-3xl sm:text-4xl animate-math-drift',
    style: { animationDelay: '0.8s' },
  },
  {
    ch: 'π',
    className: 'top-[32%] right-[14%] text-2xl sm:text-3xl animate-math-float',
    style: { animationDelay: '1.2s' },
  },
  {
    ch: '√',
    className: 'bottom-[28%] left-[6%] text-3xl animate-math-drift',
    style: { animationDelay: '0.3s' },
  },
  {
    ch: '%',
    className: 'bottom-[20%] right-[10%] text-3xl sm:text-4xl animate-math-float',
    style: { animationDelay: '1.5s' },
  },
  {
    ch: '∞',
    className: 'top-[48%] left-[12%] text-2xl animate-math-drift',
    style: { animationDelay: '2s' },
  },
  {
    ch: 'Σ',
    className: 'bottom-[40%] right-[18%] text-2xl sm:text-3xl animate-math-float',
    style: { animationDelay: '0.5s' },
  },
  { ch: 'Δ', className: 'top-[12%] left-[20%] text-xl sm:text-2xl animate-math-drift' },
];

const LoginMobileOtp: React.FC = () => {
  const [mobileRaw, setMobileRaw] = useState('');
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [otpRequested, setOtpRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/';

  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = window.setInterval(() => {
      setCooldownLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldownLeft]);

  const nationalTen = useMemo(() => normalizeNationalMobileDigits(mobileRaw), [mobileRaw]);
  const isMobileValid = nationalTen !== null;
  const mobileDisplay09 = nationalTen ? `0${nationalTen}` : '';
  const code = useMemo(() => codeDigits.join(''), [codeDigits]);
  const isCodeValid = code.length === OTP_LENGTH;
  const canResend = otpRequested && cooldownLeft === 0 && isMobileValid && !loading;

  const requestOtp = async () => {
    setError('');
    setOk('');
    setLoading(true);
    try {
      const res = await authApi.requestLoginOtp({ mobile_national: mobileRaw });
      setOtpRequested(true);
      setCooldownLeft(RESEND_SECONDS);
      setOk(res.message ?? 'کد تایید ارسال شد.');
      setCodeDigits(Array(OTP_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 30);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ارسال کد ناموفق بود.');
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpRequested) {
      await requestOtp();
      return;
    }
    setError('');
    setOk('');
    setLoading(true);
    try {
      await authApi.verifyLoginOtp({ mobile_national: mobileRaw, code });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'کد نامعتبر است.');
    } finally {
      setLoading(false);
    }
  };

  const onDigitChange = (idx: number, value: string) => {
    const d = value.replace(/\D/g, '');
    if (!d) {
      setCodeDigits((prev) => {
        const next = [...prev];
        next[idx] = '';
        return next;
      });
      return;
    }
    const digit = d[d.length - 1];
    setCodeDigits((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (idx < OTP_LENGTH - 1) inputRefs.current[idx + 1]?.focus();
  };

  const onDigitKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !codeDigits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx < OTP_LENGTH - 1) inputRefs.current[idx + 1]?.focus();
    if (e.key === 'ArrowRight' && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const onCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    text.split('').forEach((ch, i) => {
      next[i] = ch;
    });
    setCodeDigits(next);
    const last = Math.min(text.length, OTP_LENGTH) - 1;
    inputRefs.current[Math.max(last, 0)]?.focus();
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-100 via-amber-50/90 to-amber-100/80 p-4"
      dir="rtl"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgb(120 53 15 / 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(120 53 15 / 0.06) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
        }}
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        {FLOAT_SYMBOLS.map((s, i) => (
          <span
            key={i}
            className={`absolute select-none font-mono text-amber-700/25 ${s.className}`}
            style={s.style}
            dir="ltr"
          >
            {s.ch}
          </span>
        ))}
        <span
          className="absolute top-[25%] left-[30%] text-sm sm:text-base font-mono text-amber-800/20 animate-pulse-soft"
          dir="ltr"
        >
          ۱۲٬۴۵۰٬۰۰۰
        </span>
        <span
          className="absolute bottom-[35%] right-[22%] text-xs sm:text-sm font-mono text-amber-800/15 animate-pulse-soft"
          style={{ animationDelay: '1s' }}
          dir="ltr"
        >
          ۱۷٪
        </span>
      </div>
      <svg
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-36 sm:h-44 text-amber-600"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          className="login-chart-path"
          d="M0,100 C200,20 400,100 600,50 C800,0 1000,80 1200,30"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          className="login-chart-path login-chart-path--delay"
          d="M0,80 C300,100 500,30 800,60 C950,80 1100,40 1200,70"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.5"
        />
      </svg>

      <div className="relative z-10 w-full max-w-md animate-card-rise">
        <div className="relative rounded-2xl border border-amber-200/60 bg-white/90 p-6 shadow-2xl shadow-amber-900/10 backdrop-blur-sm ring-1 ring-amber-100/80 sm:p-8">
          <div
            className="pointer-events-none absolute -inset-px rounded-2xl opacity-50"
            style={{
              background:
                'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, transparent 40%, rgba(120, 53, 15, 0.06) 100%)',
            }}
          />
          <div className="relative mb-8 text-center">
            <div className="inline-flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-lg animate-icon-glow">
              {otpRequested ? <ShieldCheck className="h-9 w-9" strokeWidth={1.75} /> : <Calculator className="h-9 w-9" strokeWidth={1.75} />}
            </div>
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-slate-800">
              {otpRequested ? 'تایید کد یکبارمصرف' : 'ورود با شماره موبایل'}
            </h1>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
              {otpRequested ? 'کد ۵ رقمی پیامک‌شده را وارد کن.' : 'شماره موبایل را وارد کن تا کد تایید ارسال شود.'}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-amber-800/80">
              <TrendingUp size={14} className="shrink-0" />
              <span>ورود امن به سامانه مالی</span>
            </div>
          </div>

          <form onSubmit={submitVerify} className="relative space-y-4">
            {!otpRequested && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">شماره موبایل</label>
                <div dir="ltr" className="flex items-center rounded-xl border border-slate-300 bg-white px-3">
                  <span className="mr-2 text-sm text-slate-500">+98</span>
                  <input
                    type="text"
                    value={mobileRaw}
                    onChange={(e) => setMobileRaw(e.target.value.replace(/\D/g, '').slice(0, 14))}
                    className="w-full bg-transparent px-2 py-2.5 text-left outline-none"
                    placeholder="9123456789"
                    inputMode="numeric"
                    autoComplete="tel-national"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  می‌توانید با ۰۹۱۲…، ۹۱۲…، یا ۹۸۹… وارد کنید. شماره باید در پروفایل کاربر در سامانه ثبت شده باشد.
                </p>
                {mobileRaw.length > 0 && !isMobileValid && (
                  <p className="mt-1 text-xs text-amber-800">الگوی شماره کامل نیست.</p>
                )}
              </div>
            )}

            {!otpRequested ? (
              <button
                type="button"
                onClick={requestOtp}
                disabled={!isMobileValid || loading}
                className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-l from-amber-600 to-amber-500 py-2.5 font-medium text-white shadow-md transition hover:from-amber-700 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span
                  className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                  aria-hidden
                />
                <span className="relative inline-flex w-full items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <LoaderCircle size={18} className="animate-spin" />
                      در حال ارسال...
                    </>
                  ) : (
                    'ارسال کد تایید'
                  )}
                </span>
              </button>
            ) : (
              <>
                {mobileDisplay09 ? (
                  <p className="text-center text-sm text-slate-600">
                    <span className="text-slate-500">ارسال به: </span>
                    <span dir="ltr" className="font-mono font-semibold text-slate-800">
                      {mobileDisplay09}
                    </span>
                  </p>
                ) : null}
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">کد تایید</label>
                  <div className="grid grid-cols-5 gap-2" dir="ltr">
                    {codeDigits.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => {
                          inputRefs.current[idx] = el;
                        }}
                        type="text"
                        value={digit}
                        onChange={(e) => onDigitChange(idx, e.target.value)}
                        onKeyDown={(e) => onDigitKeyDown(idx, e)}
                        onPaste={onCodePaste}
                        className="h-12 rounded-xl border border-slate-300 bg-white text-center text-lg font-bold outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600"
                        inputMode="numeric"
                        maxLength={1}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>
                </div>
                {canResend && <p className="text-xs text-slate-500">اگر کد نرسید، ارسال مجدد را بزن.</p>}
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={loading || !canResend}
                  className="w-full min-h-[42px] rounded-xl border border-amber-300 font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                >
                  {loading ? '...' : cooldownLeft > 0 ? `ارسال مجدد تا ${cooldownLeft} ثانیه` : 'ارسال مجدد کد'}
                </button>
                <button
                  type="submit"
                  disabled={loading || !isCodeValid}
                  className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-[#a16207] py-3 font-bold text-white shadow-sm hover:bg-[#854d0e] active:bg-[#713f12] disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <LoaderCircle size={18} className="animate-spin" />
                      در حال ورود...
                    </>
                  ) : (
                    'تایید و ورود'
                  )}
                </button>
              </>
            )}

            {ok && (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                {ok}
              </p>
            )}
            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={() => navigate('/login', { replace: true, state: { from } })}
              className="w-full text-sm text-slate-600 hover:text-slate-800"
            >
              بازگشت به ورود با رمز عبور
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginMobileOtp;
